/**
 * Feature 1 — Vendor's own profile analytics.  Shows view counts by
 * month, geographic breakdown, and authenticated vs anonymous split.
 *
 * No individual viewer identity is ever stored or shown — only
 * state/country aggregates per spec §75.
 */
import { useQuery } from '@tanstack/react-query';
import PageHeader from '../../components/PageHeader';
import LoadingSpinner from '../../components/LoadingSpinner';
import { publicProfileApi } from '../../services/publicProfile';
import { vendorApi } from '../../services/vendor';
import { Link } from 'react-router-dom';


function MonthLabel({ ym }: { ym: string }) {
  const [y, m] = ym.split('-');
  return <>{new Date(Number(y), Number(m) - 1, 1).toLocaleDateString(undefined, { month: 'short', year: '2-digit' })}</>;
}


export default function VendorProfileAnalytics() {
  const { data: org } = useQuery({
    queryKey: ['vendor', 'organization'],
    queryFn: () => vendorApi.getOrganization().then((r) => r.data),
  });
  const { data, isLoading } = useQuery({
    queryKey: ['vendor', 'profile-analytics'],
    queryFn: () => publicProfileApi.getVendorAnalytics(),
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!data) {
    return null;
  }

  const maxMonth = Math.max(1, ...data.monthly_views.map((m) => m.count));
  const totalViews = data.auth_breakdown.authenticated + data.auth_breakdown.anonymous;
  const authPct = totalViews > 0
    ? Math.round((data.auth_breakdown.authenticated / totalViews) * 100)
    : 0;

  return (
    <>
      <PageHeader
        title="Profile Analytics"
        description="Aggregate views of your public profile.  No individual viewer identity is collected."
        actions={
          org?.profile_slug ? (
            <a
              href={`/v/${org.profile_slug}`}
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-xl border-2 border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              View public profile
            </a>
          ) : null
        }
      />

      {/* Monthly views */}
      <div className="rounded-2xl border border-slate-100 bg-white p-6 mb-6">
        <div className="text-sm font-semibold text-slate-900 mb-4">Views by month</div>
        <div className="flex items-end gap-1 h-32">
          {data.monthly_views.map((m) => {
            const h = (m.count / maxMonth) * 100;
            return (
              <div key={m.month} className="flex-1 flex flex-col items-center gap-1">
                <div className="w-full flex items-end h-full">
                  <div
                    className="w-full rounded-t bg-brand-400"
                    style={{ height: `${Math.max(4, h)}%` }}
                    title={`${m.month}: ${m.count}`}
                  />
                </div>
                <div className="text-[10px] text-slate-400">
                  <MonthLabel ym={m.month} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Geographic + Auth split row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div className="rounded-2xl border border-slate-100 bg-white p-6">
          <div className="text-sm font-semibold text-slate-900 mb-4">
            Top regions
          </div>
          {data.geographic_breakdown.length === 0 ? (
            <p className="text-sm text-slate-500">No views yet.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-slate-400 uppercase">
                  <th className="text-left font-medium">Country</th>
                  <th className="text-left font-medium">Region</th>
                  <th className="text-right font-medium">Views</th>
                </tr>
              </thead>
              <tbody>
                {data.geographic_breakdown.slice(0, 10).map((g, i) => (
                  <tr key={i} className="border-t border-slate-100">
                    <td className="py-2 text-slate-700">{g.country}</td>
                    <td className="py-2 text-slate-700">{g.state}</td>
                    <td className="py-2 text-right font-medium text-slate-900">
                      {g.count}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="rounded-2xl border border-slate-100 bg-white p-6">
          <div className="text-sm font-semibold text-slate-900 mb-4">
            Authenticated vs anonymous
          </div>
          <div className="text-3xl font-bold text-slate-900">
            {authPct}%
          </div>
          <div className="text-sm text-slate-500 mb-4">
            of viewers were signed in
          </div>
          <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
            <div className="h-full bg-brand-500" style={{ width: `${authPct}%` }} />
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
            <div>
              <div className="text-xs text-slate-400">Signed in</div>
              <div className="font-semibold text-slate-700">
                {data.auth_breakdown.authenticated}
              </div>
            </div>
            <div>
              <div className="text-xs text-slate-400">Anonymous</div>
              <div className="font-semibold text-slate-700">
                {data.auth_breakdown.anonymous}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
